import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import axios from 'axios'
import AppDataGrid from '../../components/common/AppDataGrid';
import { Tooltip } from '@mui/material';

const PublishResults = () => {
    const userData = sessionStorage.getItem('user');
    const userObj = JSON.parse(userData);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [exams, setExams] = useState([]);
    const [students, setStudents] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [form, setForm] = useState({ class_id: '', section_id: '', exam_id: '' });

    useEffect(() => {
        fetchClasses();
        fetchExams();
    }, []);

    useEffect(() => {
        if (form.class_id) fetchSections(form.class_id);
    }, [form.class_id]);

    const fetchClasses = async () => {
        try {
            const res = await axios.post(baseUrl + '/classes/', {
                action: 'READ',
                school_id: userObj.school_id
            });
            setClasses(res.data || []);
        } catch (err) {
            console.error('Error fetching classes:', err);
        }
    };

    const fetchSections = async (class_id) => {
        try {
            const res = await axios.post(baseUrl + '/Sections/', {
                action: 'DROPDOWNREAD',
                class_id,
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
            });
            setSections(res.data || []);
        } catch (err) {
            console.error('Error fetching sections:', err);
        }
    };

    const fetchExams = async () => {
        try {
            const res = await axios.post(baseUrl + '/exammaster', {
                action: 'READ',
                school_id: userObj.school_id,
                academic_year_id: userObj.academic_year_id,
            });
            setExams(res.data || []);
        } catch (err) {
            console.error('Error fetching exams:', err);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm(prev => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const studentRes = await axios.post(`${baseUrl}/students/`, {
                action: 'FILTER',
                class_id: form.class_id,
                section_id: form.section_id,
                academic_year_id: userObj.academic_year_id,
                school_id: userObj.school_id,
            });

            const studentsList = studentRes.data || [];

            const displayRes = await axios.get(`${baseUrl}/main/get_exam_results_filteraproval`, {
                params: {
                    school_id: userObj.school_id,
                    academic_year_id: userObj.academic_year_id,
                    class_id: form.class_id,
                    section_id: form.section_id,
                    exam_id: form.exam_id || 0
                },
            });

            const displayData = displayRes.data || [];
            let mergedStudents = [];

            if (form.exam_id) {
                // Single exam selected — one row per student
                const displayMap = {};
                displayData.forEach(entry => {
                    displayMap[entry.student_id] = {
                        is_display: entry.is_display === 'Y',
                        publish_id: entry.publish_id || null,
                    };
                });

                mergedStudents = studentsList.map(student => {
                    const dData = displayMap[student.student_id] || {};
                    return {
                        ...student,
                        row_id: `${student.student_id}_${form.exam_id}`,
                        student_name: `${student.student_first_name || ''} ${student.student_last_name || ''}`.trim(),
                        class_name: student.class_name,
                        section_name: student.section_name,
                        exam_id: form.exam_id,
                        exam_name: exams.find(e => e.exam_id == form.exam_id)?.exam_name || '',
                        is_display: dData.is_display ?? false,
                        publish_id: dData.publish_id ?? null,
                    };
                });
            } else {
                // No exam selected — show one row per student per exam from API response
                const studentMap = {};
                studentsList.forEach(s => { studentMap[s.student_id] = s; });

                mergedStudents = displayData.map(entry => {
                    const student = studentMap[entry.student_id] || {};
                    return {
                        ...student,
                        row_id: `${entry.student_id}_${entry.exam_id}`,
                        student_name: `${student.student_first_name || ''} ${student.student_last_name || ''}`.trim(),
                        class_name: student.class_name || '',
                        section_name: student.section_name || '',
                        exam_id: entry.exam_id,
                        exam_name: entry.exam_name || '',
                        is_display: entry.is_display === 'Y',
                        publish_id: entry.publish_id || null,
                        student_id: entry.student_id,
                    };
                });
            }

            setStudents(mergedStudents);
            setShowTable(true);
        } catch (err) {
            toast.error("Failed to fetch student display data.");
            console.error("handleSubmit error:", err);
        }
    };

    const handlePublish = async () => {
        // Group students by exam_id for create payloads
        const createByExam = {};
        const updatePayload = {
            publish_id: [],
            exam_id: [],
            student_id: [],
            is_display: [],
        };

        students.forEach(s => {
            if (s.publish_id) {
                updatePayload.publish_id.push(s.publish_id);
                updatePayload.exam_id.push(s.exam_id);
                updatePayload.student_id.push(s.student_id);
                updatePayload.is_display.push(s.is_display ? 'Y' : 'N');
            } else {
                if (!createByExam[s.exam_id]) {
                    createByExam[s.exam_id] = {
                        exam_id: s.exam_id,
                        student_id: [],
                        is_display: [],
                    };
                }
                createByExam[s.exam_id].student_id.push(s.student_id);
                createByExam[s.exam_id].is_display.push(s.is_display ? 'Y' : 'N');
            }
        });

        try {
            for (const examId of Object.keys(createByExam)) {
                const payload = createByExam[examId];
                if (payload.student_id.length > 0) {
                    await axios.post(`${baseUrl}/main/publishresultscreate/`, payload);
                }
            }

            if (updatePayload.publish_id.length > 0) {
                await axios.post(`${baseUrl}/main/publishresultsupdatebulk`, updatePayload);
            }

            toast.success("Publish status updated!");
        } catch (err) {
            toast.error("Failed to publish results.");
            console.error("Publish error:", err);
        }
    };

    const columns = [
        {
            field: 'student_name',
            headerName: 'Student Name',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span>{params.value || ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'class_name',
            headerName: 'Class',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span>{params.value || ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'section_name',
            headerName: 'Section',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span>{params.value || ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'exam_name',
            headerName: 'Exam',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: true,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span>{params.value || ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'is_display',
            headerName: (
                <div className="w-100 text-end">
                    <Form.Check
                        type="checkbox"
                        label="Select All"
                        className="text-nowrap"
                        checked={(() => {
                            const filtered = students.filter(item =>
                                Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase())
                            );
                            return filtered.length > 0 && filtered.every(s => s.is_display);
                        })()}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            const filteredIds = new Set(
                                students
                                    .filter(item => Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase()))
                                    .map(s => s.row_id || `${s.student_id}_${s.exam_id || 'none'}`)
                            );
                            setStudents(prev =>
                                prev.map(s => {
                                    const id = s.row_id || `${s.student_id}_${s.exam_id || 'none'}`;
                                    return filteredIds.has(id) ? { ...s, is_display: checked } : s;
                                })
                            );
                        }}
                    />
                </div>
            ),
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <Form.Check
                    type="checkbox"
                    checked={params.row.is_display}
                    onChange={() =>
                        setStudents(prev =>
                            prev.map(s =>
                                s.row_id === params.row.row_id
                                    ? { ...s, is_display: !s.is_display }
                                    : s
                            )
                        )
                    }
                />
            ),
        },
    ];


    const filteredData = students.filter(item =>
        Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <div className='pageBody'>
            <ToastContainer />
            <Container fluid>
                <Card>
                    <Card.Body className='pb-1'>
                        <form onSubmit={handleSubmit}>
                            <Row className='align-items-end'>
                                <Col xs={12}><h6 className='commonSectionTitle mb-3'>Publish Results</h6></Col>
                                <Col xs={6} md={3} className='mb-2'>
                                    <Form.Group className="commonInput mb-0">
                                        <Form.Label>Class<span className="requiredStar">*</span></Form.Label>
                                        <Form.Select size='sm' id='class_id' value={form.class_id} onChange={handleInputChange} required>
                                            <option value='' >Select Class</option>
                                            {classes.map(cls => <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={6} md={3} className='mb-2'>
                                    <Form.Group className="commonInput mb-0">
                                        <Form.Label>Section<span className="requiredStar">*</span></Form.Label>
                                        <Form.Select size='sm' id='section_id' value={form.section_id} onChange={handleInputChange} required>
                                            <option value='' >Select Section</option>
                                            {sections.map(sec => <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={6} md={3} className='mb-2'>
                                    <Form.Group className="commonInput mb-0">
                                        <Form.Label>Exam</Form.Label>
                                        <Form.Select size='sm' id='exam_id' value={form.exam_id} onChange={handleInputChange}>
                                            <option value='' >Select Exam</option>
                                            {(exams || [])
                                                .filter((exam) => exam.is_active === "Active")
                                                .map((exam) => (
                                                    <option key={exam.exam_id} value={exam.exam_id}>
                                                        {exam.exam_name}
                                                    </option>
                                                ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={6} md={3} className='mb-2 d-flex align-items-end'>
                                    <div>
                                        <Button type="submit" className="primaryBtn btn-sm">Search</Button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </Card.Body>
                </Card>

                {showTable && (
                    <>
                        <Row className='mt-3 mb-2 align-items-center'>
                            <Col md={3} className='ps-0'>
                                <Form.Control
                                    size='sm'
                                    type="text"
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </Col>
                            <Col md={9} className='text-end'>
                                {students.length > 0 && (
                                    <Button
                                        size='sm'
                                        onClick={handlePublish}
                                        style={{ backgroundColor: '#90ee90', borderColor: '#90ee90', color: '#000' }} // Light green
                                    >
                                        Publish
                                    </Button>
                                )}
                            </Col>

                        </Row>


                        <div className="commonTable mt-2">
                            <AppDataGrid
                                rows={filteredData.length ? filteredData : []}
                                columns={columns}
                                getRowId={(row) => row.row_id || `${row.student_id}_${row.exam_id || 'none'}`}
                                pageSizeOptions={[10, 20, 50]}
                                showToolbar={false}
                                pagination
                                density="compact"
                                maxHeight={450}
                            />

                        </div>
                    </>
                )}
            </Container>
        </div>

    );
};

export default PublishResults;
